import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, ShieldCheck, FileText } from 'lucide-react';

/**
 * Public Terms & Conditions page (accessible without authentication).
 * Linked from the redesigned login page.
 */
export default function TermsToJoinPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-card text-foreground" data-testid="terms-to-join-page">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/login')}
            data-testid="terms-back-btn"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="uppercase tracking-wider">Back to login</span>
          </button>
          <div className="flex items-center gap-2">
            <img src="/san-logo.jpg" alt="S.A.N Security" className="w-9 h-9 object-cover rounded-sm" />
            <span className="hidden sm:inline text-xs uppercase tracking-[0.25em] text-primary font-barlow">
              S.A.N Security
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-8">
        <div className="flex items-center gap-3 text-primary mb-3">
          <FileText className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.3em]">Legal</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-barlow tracking-widest text-foreground leading-tight">
          TERMS AND CONDITIONS<br />
          <span className="text-primary">TO JOIN</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
          By submitting an application and accepting employment with S.A.N. Security Ltd, you
          confirm that you have read, understood and agree to the following conditions.
        </p>
        <div className="mt-6 h-[2px] w-16 bg-primary" />
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <article className="bg-background/40 border border-border rounded-sm p-5 sm:p-8 space-y-7">
          <Block
            number="01"
            title="Recruitment"
            items={[
              'All information provided during the recruitment process must be accurate and truthful.',
              'Providing false or misleading information may result in your application being rejected or your employment being terminated.',
            ]}
          />
          <Block
            number="02"
            title="Right to Work"
            items={[
              'You must have the legal right to work in the United Kingdom.',
              'You must provide all requested documents before starting work.',
            ]}
          />
          <Block
            number="03"
            title="Employment"
            items={[
              'Acceptance of your application does not guarantee employment or a minimum number of working hours.',
              'Work is offered according to client requirements, availability and operational needs.',
            ]}
          />
          <Block
            number="04"
            title="Conduct"
            items={[
              'You must behave professionally, respectfully and honestly at all times.',
              'You must follow all lawful instructions issued by S.A.N. Security Ltd, supervisors and authorised client representatives.',
            ]}
          />
          <Block
            number="05"
            title="Attendance"
            items={[
              'You must attend all confirmed shifts on time.',
              'If you are unable to attend, you must notify the Company immediately.',
            ]}
          />
          <Block
            number="06"
            title="Uniform and Appearance"
            items={[
              'Employees must maintain a clean and professional appearance.',
              'Any required uniform or dress code must be followed.',
            ]}
          />
          <Block
            number="07"
            title="Company Policies"
            items={[
              'Employees must comply with all Company Policies, Procedures and Health & Safety requirements.',
              'New or updated documents must be acknowledged through the Company App.',
            ]}
          />
          <Block
            number="08"
            title="Confidentiality"
            items={[
              'Company information, client information and personal data must remain confidential at all times.',
            ]}
          />
          <Block
            number="09"
            title="Health and Safety"
            items={[
              'Employees must work safely and report any accidents, hazards or incidents immediately.',
            ]}
          />
          <Block
            number="10"
            title="Company App"
            items={[
              'Employees are responsible for maintaining accurate personal information within the Company App.',
              'Failure to use the Company App for an extended period may result in account suspension in accordance with Company procedures.',
            ]}
          />
          <Block
            number="11"
            title="Disciplinary Matters"
            items={[
              'Breaches of Company Policies or misconduct may result in disciplinary action, including dismissal where appropriate.',
            ]}
          />
          <Block
            number="12"
            title="Travel, Accommodation & Camping"
            items={[
              'Employees are responsible for arranging and paying for their own travel to the designated meeting point or event location unless otherwise confirmed in writing by S.A.N. Security Ltd.',
              'Camping equipment is not provided by S.A.N. Security Ltd unless specifically agreed in advance by the Company.',
              "Where the Company arranges or pays for travel, accommodation, ferry bookings or any other transport on behalf of an employee, any non-refundable costs resulting from a cancellation after the booking has been confirmed may be recovered from the employee in accordance with the Company's Terms and Conditions and Company Policies.",
              'Employees are responsible for taking proper care of any Company or third-party equipment issued to them and must return it when requested.',
              'Employees are expected to honour all confirmed bookings. Cancelling after travel or accommodation has been arranged may result in the recovery of costs incurred by the Company.',
            ]}
          />

          {/* Acceptance call-out */}
          <div className="rounded-sm border border-primary/40 bg-primary/5 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div className="space-y-3 text-sm leading-relaxed">
                <h3 className="text-base font-barlow tracking-widest uppercase text-primary">Acceptance</h3>
                <p>
                  By submitting your application or accepting employment with S.A.N. Security Ltd,
                  you confirm that you have read, understood and agree to comply with these Terms
                  and Conditions.
                </p>
                <p className="text-muted-foreground">
                  Failure to comply with these Terms and Conditions may result in the withdrawal of
                  employment, suspension or termination of your engagement with S.A.N. Security Ltd.
                </p>
              </div>
            </div>
          </div>
        </article>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            S.A.N. Security Ltd · www.sansecurity.uk
          </p>
          <Button
            onClick={() => navigate('/login')}
            data-testid="terms-back-bottom-btn"
            className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider text-xs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
          </Button>
        </div>
      </main>
    </div>
  );
}

function Block({ number, title, items }) {
  return (
    <section className="grid grid-cols-[auto,1fr] gap-4 sm:gap-6">
      <span className="font-mono text-primary text-sm tracking-wider pt-0.5">{number}</span>
      <div>
        <h2 className="text-sm sm:text-base font-barlow tracking-widest uppercase text-foreground">
          {title}
        </h2>
        <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground leading-relaxed">
          {items.map((it, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-primary mt-1.5 w-1 h-1 rounded-full bg-primary flex-shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
